import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HandHeart, Users, Apple, ArrowRight, ShieldCheck } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Badge, Alert, PullQuote } from '../../components/portal/ui'
import { HumanArt } from '../../components/portal/HumanArt'
import { usePublishedTestimonials, testimonialAttribution } from '../../components/portal/Voices'
import { REGISTRATION_TYPE_LABELS, type RegistrationType } from '../../services/supporterService'

const ALL_REGISTRATION_TYPES = Object.keys(REGISTRATION_TYPE_LABELS) as RegistrationType[]

// Non-text structure stays in code; title/desc/cta text comes from i18n (`pages.supportAPatient.ways[]`).
const WAY_META = [
  { icon: HandHeart, type: 'NIKSHAY_MITRA_INDIVIDUAL' as RegistrationType, anchor: 'nikshay-mitra' },
  { icon: Apple, type: 'NUTRITION_SPONSOR' as RegistrationType, anchor: 'nutrition' },
  { icon: Users, type: 'VOLUNTEER' as RegistrationType, anchor: 'community' },
]

export default function SupportAPatientPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // A real TB Supporter / volunteer voice — never fabricated.
  const { voices } = usePublishedTestimonials(3)
  const supporter = voices.find((v) => v.authorRole === 'NIKSHAY_MITRA' || v.authorRole === 'VOLUNTEER' || v.authorRole === 'CSR_PARTNER') ?? voices[0]

  const ways = t('pages.supportAPatient.ways', { returnObjects: true }) as { title: string; desc: string; cta: string }[]
  const nikshaySteps = t('pages.supportAPatient.nikshay.steps', { returnObjects: true }) as { t: string; d: string }[]

  // Post-registration handoff: ?type=<RegistrationType>&register=1 sends the
  // visitor into the guided 5-step "Become a Supporter" wizard with the chosen
  // role pre-selected, so the role picked at sign-up leads to a real supporter
  // submission. The handoff params are consumed (removed from the URL) first so
  // a refresh doesn't re-trigger.
  useEffect(() => {
    const auto = searchParams.get('register')
    const type = searchParams.get('type')
    if (auto === '1' && type && ALL_REGISTRATION_TYPES.includes(type as RegistrationType)) {
      searchParams.delete('register')
      searchParams.delete('type')
      setSearchParams(searchParams, { replace: true })
      navigate(`/become-a-supporter?type=${type}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Every "register" CTA on this hub now launches the guided wizard (the simple
  // 5-step journey) instead of the legacy inline modal, with the role pre-set.
  const openReg = (type: RegistrationType) => {
    navigate(`/become-a-supporter?type=${type}`)
  }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.supportAPatient.seo.title')}
        description={t('pages.supportAPatient.seo.description')}
        canonicalPath="/support-a-patient"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.supportAPatient', 'Support a Patient'), path: '/support-a-patient' }]}
      />
      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.supportAPatient', 'Support a Patient') }]} />
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl bg-warm-100/60 border border-warm-200 p-6">
                <HumanArt variant="reaching-out" className="w-full h-auto max-w-sm mx-auto" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              {supporter ? (
                (() => {
                  const { author, role } = testimonialAttribution(supporter)
                  return <PullQuote quote={supporter.body} author={author} role={role} to="/stories" linkLabel={t('common.readStory')} className="mb-6" />
                })()
              ) : null}
              <SectionHeading as="h1" eyebrow={t('pages.supportAPatient.hero.eyebrow')} title={t('pages.supportAPatient.hero.title')} description={t('pages.supportAPatient.hero.description')} />
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => openReg('NIKSHAY_MITRA_INDIVIDUAL')} variant="primary" icon={<HandHeart className="w-4 h-4" />}>{t('pages.supportAPatient.hero.ctaSupporter')}</Button>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Ways to support */}
      <Section className="bg-white">
        <Container>
          <div className="grid sm:grid-cols-3 gap-5">
            {ways.map((w, i) => {
              const meta = WAY_META[i] ?? WAY_META[0]
              const Icon = meta.icon
              return (
                <Card key={meta.anchor} id={meta.anchor} hover className="p-6 scroll-mt-24">
                  <span className="w-12 h-12 rounded-xl bg-[rgb(232_252_243)] text-[rgb(0_138_88)] flex items-center justify-center mb-4" aria-hidden="true">
                    <Icon className="w-6 h-6" />
                  </span>
                  <h3 className="font-bold text-snow-900">{w.title}</h3>
                  <p className="text-sm text-snow-600 mt-2">{w.desc}</p>
                  <button onClick={() => openReg(meta.type)} className="text-sm font-medium text-[rgb(15_76_129)] hover:underline mt-3 inline-block">
                    {w.cta} →
                  </button>
                </Card>
              )
            })}
          </div>
        </Container>
      </Section>

      {/* Ni-kshay Mitra detail */}
      <Section id="nikshay-mitra" className="bg-snow-50">
        <Container>
          <Card className="p-8 bg-gradient-to-br from-[rgb(240_252_255)] to-white scroll-mt-24">
            <Badge tone="teal" className="mb-3"><ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> {t('pages.supportAPatient.nikshay.badge')}</Badge>
            <h2 className="text-2xl font-bold text-snow-900">{t('pages.supportAPatient.nikshay.title')}</h2>
            <p className="text-snow-600 mt-3 max-w-2xl">
              {t('pages.supportAPatient.nikshay.body')}
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              {nikshaySteps.map((s, i) => (
                <div key={i} className="rounded-xl bg-white border border-snow-200 p-5">
                  <p className="font-semibold text-snow-900">{s.t}</p>
                  <p className="text-sm text-snow-600 mt-1">{s.d}</p>
                </div>
              ))}
            </div>
            <Alert tone="info" title={t('pages.supportAPatient.nikshay.alertTitle')}>
              {t('pages.supportAPatient.nikshay.alertBody')}
            </Alert>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={() => openReg('NIKSHAY_MITRA_INDIVIDUAL')} variant="primary" icon={<ArrowRight className="w-4 h-4" />}>{t('pages.supportAPatient.nikshay.ctaRegister')}</Button>
              <Button onClick={() => openReg('NIKSHAY_MITRA_NGO')} variant="outline">{t('pages.supportAPatient.nikshay.ctaNgo')}</Button>
              <Button to="/become-a-supporter?track=1" variant="ghost">{t('pages.becomeSupporter.step5.lookupTitle')}</Button>
              <Button to="/about-tb" variant="ghost">{t('pages.supportAPatient.nikshay.ctaUnderstand')}</Button>
            </div>
          </Card>
        </Container>
      </Section>

      {/* Nutrition */}
      <Section id="nutrition" className="bg-white">
        <Container>
          <Card className="p-8 scroll-mt-24">
            <Badge tone="green" className="mb-3"><Apple className="w-3.5 h-3.5" aria-hidden="true" /> {t('pages.supportAPatient.nutrition.badge')}</Badge>
            <h2 className="text-2xl font-bold text-snow-900">{t('pages.supportAPatient.nutrition.title')}</h2>
            <p className="text-snow-600 mt-3 max-w-2xl">
              {t('pages.supportAPatient.nutrition.body')}
            </p>
            <div className="mt-5">
              <Button onClick={() => openReg('NUTRITION_SPONSOR')} variant="secondary">{t('pages.supportAPatient.nutrition.cta')}</Button>
            </div>
          </Card>
        </Container>
      </Section>
    </PortalLayout>
  )
}